import React, { useMemo } from "react";
import { Background, Controls, Handle, Position, ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { factLabel, valueLabel } from "./proveFactLabels";
import { isProveFinal } from "./proveFinality";
import DecisionConditions from "./DecisionConditions";
import { productDialogue } from "./productFactDialogue";

const GREEN = "#1a73e8";
const BLUE = "#1a73e8";
const ORANGE = "#6b7280";

function formatCode(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `${digits.slice(0, 4)}.${digits.slice(4, 6)}-${digits.slice(6)}`;
  if (digits.length === 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
  return digits;
}

function ProofNode({ data }) {
  const tone = data.tone || "default";
  const palette = {
    proved: { border: GREEN, background: "#eef6ff", color: "#214c83" },
    pending: { border: "#9ca3af", background: "#f7f7f8", color: "#5b6470" },
    rule: { border: BLUE, background: "#eef6ff", color: "#214c83" },
    evidence: { border: "#7c8da5", background: "#f6f8fb", color: "#39485c" },
    compare: { border: "#9ca3af", background: "#f7f7f8", color: "#5b6470" },
    default: { border: "var(--audit-border)", background: "var(--audit-bg)", color: "var(--audit-text)" },
  }[tone];
  return (
    <div
      title={data.tooltip || ""}
      style={{
        width: data.width || 148,
        border: `1.5px solid ${palette.border}`,
        borderRadius: 10,
        background: palette.background,
        color: palette.color,
        padding: "8px 10px",
        textAlign: "center",
        fontSize: 11,
        lineHeight: 1.4,
      }}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="target" id="top" position={Position.Top} style={{ opacity: 0 }} />
      {data.code && (
        <div style={{ fontFamily: "JetBrains Mono, Consolas, monospace", fontSize: 13, fontWeight: 800 }}>
          {formatCode(data.code)}
        </div>
      )}
      <div style={{ fontSize: data.code ? 10.5 : 11.5, fontWeight: data.code ? 650 : 750 }}>
        {data.title}
      </div>
      {data.sub && <div style={{ marginTop: 2, color: "var(--audit-text-muted)", fontSize: 9.8 }}>{data.sub}</div>}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="source" id="down" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

const nodeTypes = { proof: ProofNode };

function text(value, fallback = "") {
  const result = String(value || "").trim();
  return result || fallback;
}

function treeFrom(result) {
  return result?.classification_tree || result?.apparel_tree || {};
}

export function buildProveGraph(result, query) {
  const tree = treeFrom(result);
  const prove = result?.prove || {};
  const proof = prove.proof || {};
  const ruleSet = prove.rule_set || {};
  const proved = isProveFinal(result);
  const dialogue = productDialogue(tree);
  const ruleReview = dialogue.status === "rule_coverage_required";
  const tone = proved ? "proved" : "pending";
  const nodes = [];
  const edges = [];
  const column = 214;

  nodes.push({
    id: "input",
    type: "proof",
    position: { x: 0, y: 0 },
    data: { title: text(query, "분류 품목"), sub: "사용자 입력", width: 164 },
  });

  const interpretedCount = Number(proof.interpreted_fact_count || Object.keys(result?.interpreted_facts || {}).length || 0);
  const confirmedCount = Number(proof.confirmed_fact_count || Object.keys(result?.confirmed_facts || {}).length || 0);
  const documentCount = Number(proof.document_fact_count || 0);
  nodes.push({
    id: "facts",
    type: "proof",
    position: { x: column, y: 0 },
    data: {
      title: `확인 사실 ${confirmedCount}건 · 문서 ${documentCount}건`,
      sub: `추출 후보 ${interpretedCount}건 · 확인 상태 별도`,
      tone: "evidence",
      tooltip: "LLM은 관찰 가능한 사실만 제안하며 수치·법률 판정은 수행하지 않습니다.",
    },
  });
  edges.push({
    id: "input-facts",
    source: "input",
    target: "facts",
    label: Number(proof.prompt_stage_count || 1) === 2 ? "2단계 사실추출" : "사실추출",
    style: { stroke: BLUE, strokeWidth: 1.5 },
  });

  nodes.push({
    id: "rules",
    type: "proof",
    position: { x: column * 2, y: 0 },
    data: {
      title: "검수 결정트리",
      sub: "분류 조건과 적용 범위 확인",
      tone: "rule",
      tooltip: [ruleSet.revision, ruleSet.reason].filter(Boolean).join("\n"),
    },
  });
  edges.push({
    id: "facts-rules",
    source: "facts",
    target: "rules",
    label: "사실만 입력",
    style: { stroke: BLUE, strokeWidth: 1.5 },
  });

  const path = [
    ["supported_chapter", "류"],
    ["supported_hs4", "호"],
    ["supported_hs6", "소호"],
    ["supported_hsk", proved ? "세번 · PROVED" : "세번 후보"],
  ].filter(([key]) => text(tree[key]));
  let previous = "rules";
  path.forEach(([key, label], index) => {
    const id = `path-${key}`;
    nodes.push({
      id,
      type: "proof",
      position: { x: column * (3 + index), y: 0 },
      data: { code: tree[key], title: label, tone: key === "supported_hsk" ? tone : "default" },
    });
    edges.push({
      id: `${previous}-${id}`,
      source: previous,
      target: id,
      style: { stroke: proved ? GREEN : ORANGE, strokeWidth: 1.6 },
    });
    previous = id;
  });

  if (!path.length) {
    nodes.push({
      id: "status",
      type: "proof",
      position: { x: column * 3, y: 0 },
      data: {
        title: ruleReview ? "자동 판단 규칙 추가 검토 필요" : tree.action === "out_of_scope" ? "검수 트리 범위 밖" : "추가 사실 필요",
        sub: ruleReview ? dialogue.description : "상품 사실과 적용 가능한 경로를 확인해 주세요.",
        tone: "pending",
      },
    });
    edges.push({ id: "rules-status", source: "rules", target: "status", style: { stroke: ORANGE, strokeWidth: 1.6 } });
    previous = "status";
  }

  const questions = dialogue.questions.slice(0, 3);
  questions.forEach((question, index) => {
    const key = question.fact_key || question.key || `question-${index + 1}`;
    const title = question.question || question.prompt_ko || question.label || key;
    const id = `question-${index}`;
    nodes.push({
      id,
      type: "proof",
      position: { x: column * 2.5 + index * 180, y: 132 },
      data: { title, sub: "확인할 상품 정보", tone: "pending", width: 165, tooltip: title },
    });
    edges.push({
      id: `rules-${id}`,
      source: "rules",
      sourceHandle: "down",
      target: id,
      targetHandle: "top",
      label: "확인 필요",
      style: { stroke: ORANGE, strokeDasharray: "4 3" },
    });
  });

  const revision = text(ruleSet.revision || tree.revision);
  const callout = proved
    ? `PROVED · 검수 결정트리가 ${formatCode(tree.supported_hsk)}을 지지합니다.${revision ? ` 규칙 ${revision}` : ""}`
    : ruleReview ? "자동 판단 규칙의 추가 검토가 필요합니다. 남은 법률 판단을 사용자에게 대신 묻지 않으며 세번을 확정하지 않습니다."
    : tree.action === "out_of_scope"
      ? "현재 검수 결정트리 범위 밖입니다. 후보는 참고용이며 세번을 확정하지 않습니다."
      : `추가 사실이 필요합니다. ${questions.length ? `${questions.length}개 갈림질문에 답하면 다시 판정합니다.` : "자동 확정하지 않습니다."}`;
  return { nodes, edges, callout };
}

export default function ProveDecisionGraph({ result, query }) {
  const { nodes, edges, callout } = useMemo(() => buildProveGraph(result, query), [result, query]);
  return (
    <section className="aiw-prove-graph" aria-label="PROVE 판정 근거 그래프">
      <div className="aiw-prove-graph-head">
        <strong>PROVE 판정 근거</strong>
        <span>사실 → 규칙 → 코드 경로와 보류 사유</span>
      </div>
      {Object.keys(result?.confirmed_facts || {}).length > 0 && (
        <div style={{ padding: "10px 14px", fontSize: 12, lineHeight: 1.7 }}>
          <strong>사용자가 확인한 사실</strong>
          <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
            {Object.entries(result.confirmed_facts).map(([key, value]) => (
              <li key={key}>{factLabel(key)}: {valueLabel(value, key)}</li>
            ))}
          </ul>
          <span>아래는 계산 경로 요약입니다. 적용 조건과 원문은 판정 근거에서 함께 확인하세요.</span>
        </div>
      )}
      <div className="aiw-prove-graph-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.14 }}
          minZoom={0.3}
          maxZoom={1.4}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={18} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      <div className="aiw-prove-callout">{callout}</div>
      <DecisionConditions tree={result?.classification_tree || result?.apparel_tree} />
    </section>
  );
}
